'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useTranslations } from 'next-intl';
import { useCartStore } from '../Cart';
import { analytics } from 'lib/analytics/analytics';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import styles from './Cart.module.css';

export const CartIcon = ({
  className,
  iconTheme,
}: {
  className?: string;
  iconTheme?: 'light' | 'dark';
}) => {
  const t = useTranslations('common');
    const router = useRouter();
    const internalPathname = useInternalPathname();
    const showCart = useCartStore((s) => s.showCart);
    const cartQuantity = useCartStore((s) => s.cart?.totalQuantity ?? 0);
    const { regionSlug, region } = useRegionLocale();
    const [isBloodVisionPage, setIsBloodVisionPage] = useState(false);
    const [bloodVisionCartCount, setBloodVisionCartCount] = useState(0);

    // Detect if we're on a blood-vision/buy/${country} page
    useEffect(() => {
      const path =
        internalPathname.length > 1
          ? internalPathname.replace(/\/$/, '')
          : internalPathname;
      const isOnBloodVisionBuyPage =
        (path === '/blood-vision/buy' ||
          path.startsWith('/blood-vision/buy/')) &&
        region === 'IN';

      // If NOT on blood-vision page and localStorage has blood-vision data, clear it
      if (!isOnBloodVisionBuyPage) {
        try {
          const hasBloodVisionCart = localStorage.getItem(
            'bloodVisionIndiaCart',
          );
          const hasBloodVisionPincode = localStorage.getItem(
            'bloodVisionIndiaPincode',
          );
          const hasBloodVisionCheckout = localStorage.getItem(
            'bloodVisionIndiaCheckoutData',
          );

          if (
            hasBloodVisionCart ||
            hasBloodVisionPincode ||
            hasBloodVisionCheckout
          ) {
            console.log(
              '[Cart Isolation] Not on Blood Vision page - clearing Blood Vision data instantly',
            );
            // Clear localStorage
            localStorage.removeItem('bloodVisionIndiaCart');
            localStorage.removeItem('bloodVisionIndiaPincode');
            localStorage.removeItem('bloodVisionIndiaCheckoutData');
            // Clear sessionStorage (including pincode input)
            sessionStorage.removeItem('bloodVisionActiveSession');
            sessionStorage.removeItem('bloodVisionLastVisit');
            sessionStorage.removeItem('blood_vision_india_pincode'); // Clear pincode input value
          }
        } catch (error) {
          console.error('Error clearing Blood Vision cart:', error);
        }
      }

      // Update state
      setIsBloodVisionPage(isOnBloodVisionBuyPage);

      // Update blood vision cart count (always, so it's ready when user navigates)
      const updateBloodVisionCount = () => {
        try {
          const cartData = localStorage.getItem('bloodVisionIndiaCart');
          if (cartData) {
            const cart = JSON.parse(cartData);
            const count = Object.values(cart).reduce(
              (sum: number, item: any) => sum + (item.quantity || 0),
              0,
            );
            setBloodVisionCartCount(count);
          } else {
            setBloodVisionCartCount(0);
          }
        } catch (error) {
          console.error('Error reading Blood Vision cart:', error);
          setBloodVisionCartCount(0);
        }
      };

      updateBloodVisionCount();

      // Listen for storage changes to update count in real-time
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'bloodVisionIndiaCart') {
          updateBloodVisionCount();
        }
      };

      window.addEventListener('storage', handleStorageChange);

      // 'storage' only fires cross-tab; this CustomEvent covers same-tab cart writes.
      window.addEventListener('bloodVisionCartUpdated', updateBloodVisionCount);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener(
          'bloodVisionCartUpdated',
          updateBloodVisionCount,
        );
      };
    }, [internalPathname, region]);

    const onCartClick = () => {
      analytics.track(`Cart Icon - CLICK`);

      if (isBloodVisionPage) {
        // On blood-vision pages, navigate to blood vision checkout
        // Check if pincode is provided
        try {
          const pincodeData = localStorage.getItem('bloodVisionIndiaPincode');
          if (pincodeData) {
            const parsedPincode = JSON.parse(pincodeData);
            // Navigate to checkout only if pincode is provided and not default
            if (parsedPincode.pincode && !parsedPincode.is_default_pincode) {
              router.push(`/${regionSlug}/blood-vision/buy/checkout`);
            } else {
              // User hasn't provided pincode yet, stay on the page
              console.log('[Cart] User needs to provide pincode first');
              // Optionally scroll to pincode input or show a message
            }
          }
        } catch (error) {
          console.error('Error reading pincode data:', error);
        }
      } else {
        // On regular pages, show global cart
        showCart();
      }
    };

    // Determine which cart count to show
    const displayCount = isBloodVisionPage
      ? bloodVisionCartCount
      : cartQuantity;

    const cartAriaLabel =
      displayCount > 0
        ? t('header.openCartWithCount', { count: displayCount })
        : t('header.openCart');

    return (
      <button
        type="button"
        className={[styles.cartIcon, className].filter(Boolean).join(' ')}
        data-icon-theme={iconTheme === 'light' ? 'light' : 'dark'}
        onClick={onCartClick}
        aria-label={cartAriaLabel}
      >
        {/* bagicon (design-supplied), inlined so it picks up currentColor per
            header theme. Native 32x32, no scaling. */}
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M15.7778 6C13.9398 6 12.4444 7.49533 12.4444 9.33333V10.4444H8.55556C8.24867 10.4444 8 10.6931 8 11V25.4444C8 25.7513 8.24867 26 8.55556 26H23C23.3069 26 23.5556 25.7513 23.5556 25.4444V11C23.5556 10.6931 23.3069 10.4444 23 10.4444H19.1111V9.33333C19.1111 7.49533 17.6158 6 15.7778 6ZM13.5556 9.33333C13.5556 8.108 14.5524 7.11111 15.7778 7.11111C17.0031 7.11111 18 8.108 18 9.33333V10.4444H13.5556V9.33333ZM22.4444 11.5556V24.8889H9.11111V11.5556H12.4444V13.2222C12.4444 13.5291 12.6931 13.7778 13 13.7778C13.3069 13.7778 13.5556 13.5291 13.5556 13.2222V11.5556H18V13.2222C18 13.5291 18.2487 13.7778 18.5556 13.7778C18.8624 13.7778 19.1111 13.5291 19.1111 13.2222V11.5556H22.4444Z"
            fill="currentColor"
          />
        </svg>
        {displayCount > 0 ? (
          <span className={styles.cartCountBadge} aria-hidden="true">
            {displayCount}
          </span>
        ) : null}
      </button>
    );
};
